<template>
    <!--
        THE ADMIN SHELL'S LEFT COLUMN: a brand row, the grouped menu, and the signed-in user at
        the bottom — the three things a console's sidebar is for, in the order they are looked at.

        It paints `--ui-bg-chrome` — the project surface `theme/theme.css` declares and
        the house theme separates from the canvas. THE FRAME IS ONE SURFACE, and in
        this theme that surface is the PANEL colour in both modes: white in light, and in dark
        the same mix the cards take, both standing on a darker canvas. The hairline on the
        right edge is the seam between the frame and the page. A theme that says nothing leaves
        the variable at `--ui-bg` and gets the flat single-surface shell instead.

        Opaque, not a tint over the canvas: on mobile this column is the overlay.
    -->
    <nav class="flex flex-col w-64 shrink-0 border-r border-default bg-(--ui-bg-chrome)">
        <div class="h-(--ui-header-height) shrink-0 flex items-center gap-2.5 px-4 border-b border-default">
            <img src="/logo.svg" alt="" class="size-6" >
            <div class="leading-tight min-w-0 flex-1">
                <p class="text-sm font-semibold text-highlighted truncate">{{ $t('brand.product_name') }}</p>
                <p class="font-mono text-[10px] uppercase tracking-widest text-dimmed truncate">{{ $t('brand.tagline') }}</p>
            </div>

            <!--
                THE WAY OUT, on mobile only. Open, this column IS the overlay and it covers the
                topbar's hamburger, so the affordance that opened the menu cannot also close it.
                The X lives where the eye already is — in the brand row, opposite the mark.
            -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-x"
                :aria-label="$t('layout.close_menu')"
                class="-mr-1 shrink-0"
                @click="closeMenu"
            />
        </div>

        <div class="flex flex-col gap-5 flex-1 overflow-y-auto px-3 py-4">
            <div v-for="(group, index) in groups" :key="`group-${index}`" class="flex flex-col gap-1">
                <p
                    v-if="group.labelKey"
                    class="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-widest text-muted"
                >{{ $t(group.labelKey) }}</p>

                <UNavigationMenu
                    orientation="vertical"
                    :items="group.items"
                    :ui="{ link: 'py-2' }"
                />
            </div>
        </div>

        <!--
            The signed-in user, where an admin console puts it. It carries the sign-out actions
            and the locale switch too — one deliberate place for them, so the topbar does not
            repeat the same menu.
        -->
        <div class="shrink-0 border-t border-default p-3">
            <UDropdownMenu :items="userMenu" :content="{ align: 'start', side: 'top' }" class="w-full">
                <button
                    type="button"
                    class="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-elevated transition-colors"
                >
                    <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary text-xs font-semibold">
                        {{ initials }}
                    </span>

                    <span class="min-w-0 flex-1 leading-tight">
                        <span class="block truncate text-sm font-medium text-highlighted">{{ userName }}</span>
                        <span class="block truncate font-mono text-[10px] uppercase tracking-widest text-muted">{{ userRole }}</span>
                    </span>

                    <UIcon name="i-lucide-chevrons-up-down" class="size-4 shrink-0 text-dimmed" />
                </button>
            </UDropdownMenu>
        </div>
    </nav>
</template>

<script lang="ts" setup>
    import type { DropdownMenuItem } from '@nuxt/ui'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { useLayout, useNavigation } from '@/layouts/layout'
    import { signOut } from '@/libs/auth'
    import type { AvailableLocales } from '@/libs/i18n'
    import { getRoleTag } from '@/libs/user'
    import { useAppStore } from '@/stores/app'

    /**
     * The administration menu. The MODEL lives in `layouts/layout.ts` (`NAV_GROUPS`, rendered
     * through `useNavigation()` so the `Users` gate and the Issues badge are applied once), because
     * the Cmd+K palette lists the same entries — one list, so a section can never be named one
     * thing in the menu and another in the palette.
     *
     * It is a `UNavigationMenu orientation="vertical"` per group, not one menu with `type:
     * 'label'` rows: a group is a separate list with its own header, and rendering the header
     * ourselves is what lets it be the small mono uppercase signpost the house theme asks for
     * rather than the component's own bold label slot.
     */
    const { t, locale }           = useI18n()
    const appStore                = useAppStore()
    const { isMobile, closeMenu } = useLayout()
    const { groups }              = useNavigation()

    const locales: { code: AvailableLocales, label: string }[] = [
        { code: 'sl', label: 'Slovenščina' },
        { code: 'en', label: 'English' },
    ]

    const userName = computed((): string =>
        appStore.session.user?.name || appStore.session.user?.email || t('auth.account'))

    /**
     * The second line of the user row: the roles the session carries (§8.2), or the e-mail for an
     * account that holds assignments only — a line that is sometimes empty reads as a bug.
     */
    const userRole = computed((): string => {
        const roles = appStore.session.roles.map((role) => getRoleTag(role).label)

        return roles.length > 0 ? roles.join(' · ') : (appStore.session.user?.email ?? '')
    })

    const initials = computed((): string => userName.value
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join(''))

    const localeMenu = computed((): DropdownMenuItem[] => locales.map((item) => ({
        label:    item.label,
        type:     'checkbox' as const,
        checked:  locale.value === item.code,
        onSelect: () => appStore.changeLocale(item.code),
    })))

    const userMenu = computed((): DropdownMenuItem[][] => [
        [{
            label: appStore.session.user?.email ?? '',
            type:  'label' as const,
        }],
        [{
            label: t('nav.profile'),
            icon:  'i-lucide-user',
            to:    { name: 'profile' },
        }, {
            label:    t('nav.language'),
            icon:     'i-lucide-languages',
            children: localeMenu.value,
        }],
        [{
            // D78 — this device only: the server permanently revokes this portal session's
            // record and clears the `__Host-tpm_session` cookie. Local to TPM — the IT Team ID
            // SSO session is untouched, so the next Sign in may not ask for a password.
            label:    t('nav.sign_out'),
            icon:     'i-lucide-log-out',
            onSelect: () => { void signOut() },
        }, {
            // D78 — every one of *my TPM sessions*, on every device: the server bumps the
            // caller's `auth_epoch` and revokes each of their portal sessions. Still local to
            // TPM: no IdP call, no effect on IT Team ID or any other application — which is why
            // the label names the scope and never reads as a global sign-out (handoff §7.1).
            label:    t('nav.sign_out_all_tpm'),
            icon:     'i-lucide-shield-alert',
            color:    'error' as const,
            onSelect: () => { void signOut(true) },
        }],
    ])
</script>
